// Helpers de la feature "Facturación y Cobro" (frontend).

export const CHARGE_MODES = [
  { value: 'recolectado', label: 'Por lo recolectado', icon: 'inventory_2' },
  { value: 'entregado', label: 'Por lo entregado', icon: 'local_shipping' },
];

export const CHARGE_MODE_LABELS = {
  recolectado: 'Por recolección',
  entregado: 'Por entrega',
};

export const PAYMENT_STATUS_LABELS = {
  unpaid: 'Impaga',
  partially_paid: 'Pago parcial',
  in_review: 'En revisión',
  paid: 'Pagada',
  overdue: 'Vencida',
  cancelled: 'Anulada',
};

export const PAYMENT_STATUS_TONE = {
  unpaid: 'warning',
  partially_paid: 'info',
  in_review: 'info',
  paid: 'success',
  overdue: 'error',
  cancelled: 'neutral',
};

// Filtro de estado (pill-row). value '' = todas. Orden = recorrido natural
// del cobro: pendiente → parcial → pagada, y al final los casos especiales.
export const PAYMENT_STATUS_FILTERS = [
  { value: '', label: 'Todas' },
  { value: 'unpaid', label: 'Impagas' },
  { value: 'partially_paid', label: 'Parciales' },
  { value: 'in_review', label: 'En revisión' },
  { value: 'paid', label: 'Pagadas' },
  { value: 'overdue', label: 'Vencidas' },
  { value: 'cancelled', label: 'Anuladas' },
];

export const CHARGE_MODE_FILTERS = [
  { value: '', label: 'Todos los modos' },
  { value: 'recolectado', label: 'Por recolección' },
  { value: 'entregado', label: 'Por entrega' },
];

// Nombre del comercio de una nota, tolerando distintas formas del payload
// (objeto poblado, id suelto) y resolviendo contra un diccionario id→nombre.
export const resolveCompanyName = (note, companyMap = {}) => {
  if (!note) return '';
  if (note.company?.name) return note.company.name;
  if (note.companyName) return note.companyName;
  const id = typeof note.companyId === 'object' ? note.companyId?._id : note.companyId;
  return companyMap[id] || '';
};

export const formatCurrency = (amount, currency = 'CLP') => {
  if (amount === undefined || amount === null) amount = 0;
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(Math.round(amount));
  } catch {
    // Moneda no reconocida por Intl: fallback con el código.
    return `${currency} ${Math.round(amount).toLocaleString('es-CL')}`;
  }
};

// ── Vencimiento de la nota (derivado en billing) ──
//
// "Vencida" NO es un estado guardado: billing la deriva en cada lectura (`isOverdue`,
// `daysOverdue`, `agingBucket`) con su reloj, y nunca escribe paymentStatus='overdue'. El
// front no la recalcula: con el reloj y el huso del navegador la lista, el detalle y el
// dashboard podrían contradecirse para la misma nota.

// Estados de deuda: la nota sigue debiéndose (incluye la que tiene un pago en revisión).
const DEBT_PAYMENT_STATUSES = ['unpaid', 'partially_paid', 'overdue', 'in_review'];

/** ¿La nota es deuda del comercio? (impaga, parcial o con pago en revisión, y no anulada). */
export const isDebtNote = (note) =>
  !!note && note.status !== 'cancelled' && DEBT_PAYMENT_STATUSES.includes(note.paymentStatus);

/**
 * ¿La nota tiene un monto a cobrar? `false` solo con un total numérico <= 0 (p. ej. una
 * compensación COD que iguala el subtotal). Mismo criterio que `hasAmountToCollect` de
 * billing: una nota así no es cobrable, no vence y no admite recordatorios.
 */
export const hasAmountToCollect = (note) =>
  !(typeof note?.totalAmount === 'number' && note.totalAmount <= 0);

/**
 * Estado de pago a MOSTRAR: 'overdue' si billing marcó la nota como vencida; si no, el
 * paymentStatus guardado. Es lo que dibuja PaymentStatusBadge cuando recibe la nota.
 */
export const displayPaymentStatus = (note) => {
  if (!note) return undefined;
  return note.isOverdue === true ? 'overdue' : note.paymentStatus;
};

/**
 * 'YYYY-MM-DD' → 'dd-mm-aaaa', recortando el string. Nunca pasa por `Date`: `dueDateLocal` ya
 * es el día local del OPERADOR, y `new Date('2026-10-15')` es medianoche UTC, que en
 * America/Santiago se muestra como el día anterior. `null` si no tiene esa forma.
 */
export const formatDayKey = (dayKey) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey ?? ''));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
};

/** Día de hoy 'YYYY-MM-DD' en el huso del navegador. */
const browserDayKey = (now = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

/**
 * Qué decir del vencimiento de una nota en un listado:
 *   · `{ kind: 'overdue', days, dueDate }` si billing la marcó vencida;
 *   · `{ kind: 'lapsed', dueDate }` si está en revisión y su vencimiento ya pasó;
 *   · `{ kind: 'due', dueDate }` si es deuda y tiene vencimiento;
 *   · `null` si no hay nada que decir (pagada, anulada, sin monto a cobrar o sin vencimiento).
 * `dueDate` va ya formateado 'dd-mm-aaaa' (o null si falta).
 *
 * @param {object} note
 * @param {string} [today] 'YYYY-MM-DD'; por defecto, hoy en el navegador
 */
export const dueDateStatus = (note, today = browserDayKey()) => {
  if (!note || note.status === 'cancelled') return null;
  const dueDate = formatDayKey(note.dueDateLocal);
  if (note.isOverdue === true) {
    return { kind: 'overdue', days: Number(note.daysOverdue) || 0, dueDate };
  }
  // Total 0: no hay nada que pagar y billing nunca la marca vencida. Con la fecha pasada se
  // leería "Vence el <fecha pasada>"; con una futura, un vencimiento de una deuda que no existe.
  if (!hasAmountToCollect(note)) return null;
  if (dueDate && isDebtNote(note)) {
    // Billing nunca marca vencida una nota en revisión (no es cobrable), así que su
    // `isOverdue` no dice si la fecha ya pasó: sin esto, una fecha pasada se leería "Vence
    // el …". Se compara con hoy en el navegador; frente al día del operador difiere a lo
    // sumo en uno y solo cambia el tiempo verbal. Las cobrables siguen el criterio de billing.
    if (note.paymentStatus === 'in_review' && note.dueDateLocal < today) {
      return { kind: 'lapsed', dueDate };
    }
    return { kind: 'due', dueDate };
  }
  return null;
};

export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ── Presets de rango de fecha (resueltos en el front, en el tz local del operador) ──

const pad = (n) => String(n).padStart(2, '0');
const toYMD = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Domingo de la semana que contiene `ref` (semana DOM–SÁB).
//
// Antes era lunes–domingo. Se unificó en domingo–sábado, que es la semana con la que ya
// trabajaba el pago a repartidores: la plataforma tenía las dos convenciones conviviendo, y
// la tarifa escalonada por volumen necesita UNA sola — el backend cuenta el volumen del
// ciclo con `sundaySaturdayWeek` y rechaza emitir una nota que no cubra ese ciclo exacto.
const sundayOf = (ref) => {
  const d = new Date(ref);
  d.setDate(d.getDate() - d.getDay()); // getDay(): 0 = domingo
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

export const DATE_PRESETS = [
  { value: 'last_week', label: 'Semana completa (Dom–Sáb) anterior' },
  { value: 'two_weeks', label: 'Dos semanas completas anteriores' },
  { value: 'first_fortnight', label: 'Primera quincena (1–15) del mes' },
  { value: 'second_fortnight', label: 'Segunda quincena (16–fin) del mes' },
  { value: 'previous_month', label: 'Mes anterior completo' },
  { value: 'custom', label: 'Rango personalizado' },
];

// ── Moneda del operador (derivada del país) ──

const ISO_TO_CURRENCY = {
  CL: 'CLP', AR: 'ARS', PE: 'PEN', CO: 'COP', VE: 'VES', UY: 'UYU', MX: 'MXN',
};
const NAME_TO_CURRENCY = {
  chile: 'CLP', argentina: 'ARS', peru: 'PEN', 'perú': 'PEN', colombia: 'COP',
  venezuela: 'VES', uruguay: 'UYU', mexico: 'MXN', 'méxico': 'MXN',
};

export const currencyForCountry = (country) => {
  if (!country) return undefined;
  const t = String(country).trim();
  if (t.length === 2) return ISO_TO_CURRENCY[t.toUpperCase()];
  return NAME_TO_CURRENCY[t.toLowerCase()];
};

// ── Locale de formato del operador (derivado del país) ──
// Tabla explícita en vez de construir `es-${code}`: un código desconocido
// generaría un tag BCP-47 inventado y lo recibiría Intl.
const ISO_TO_LOCALE = {
  CL: 'es-CL', AR: 'es-AR', PE: 'es-PE', CO: 'es-CO', VE: 'es-VE', UY: 'es-UY', MX: 'es-MX',
};
const NAME_TO_LOCALE = {
  chile: 'es-CL', argentina: 'es-AR', peru: 'es-PE', 'perú': 'es-PE', colombia: 'es-CO',
  venezuela: 'es-VE', uruguay: 'es-UY', mexico: 'es-MX', 'méxico': 'es-MX',
};

/**
 * Locale de formato (Intl) para un país dado por nombre o código ISO-2.
 * Los países sin entrada caen a 'es-CL', que es el formato que la app usaba
 * fijo antes de la expansión a Argentina.
 * @param {string} [country] 'CL' | 'Chile' | 'AR' | 'Argentina' | …
 */
export const localeForCountry = (country) => {
  if (!country) return 'es-CL';
  const t = String(country).trim();
  const hit = t.length === 2 ? ISO_TO_LOCALE[t.toUpperCase()] : NAME_TO_LOCALE[t.toLowerCase()];
  return hit || 'es-CL';
};

// Moneda del operador: la del documento si está, si no la derivada del país.
export const resolveOperatorCurrency = (logisticCompany) => {
  if (!logisticCompany) return 'CLP';
  return (
    logisticCompany.currency ||
    currencyForCountry(logisticCompany.operationsCountry) ||
    currencyForCountry(logisticCompany.country) ||
    currencyForCountry(logisticCompany.operationsAddresses?.[0]?.country) ||
    'CLP'
  );
};

// Devuelve { start, end } en 'YYYY-MM-DD' para el preset dado.
export const rangeFromPreset = (preset, ref = new Date()) => {
  const y = ref.getFullYear();
  const m = ref.getMonth();
  switch (preset) {
    case 'last_week': {
      const sun = sundayOf(ref);
      return { start: toYMD(addDays(sun, -7)), end: toYMD(addDays(sun, -1)) };
    }
    case 'two_weeks': {
      const sun = sundayOf(ref);
      return { start: toYMD(addDays(sun, -14)), end: toYMD(addDays(sun, -1)) };
    }
    case 'first_fortnight':
      return { start: toYMD(new Date(y, m, 1)), end: toYMD(new Date(y, m, 15)) };
    case 'second_fortnight':
      return { start: toYMD(new Date(y, m, 16)), end: toYMD(new Date(y, m + 1, 0)) };
    case 'previous_month':
      return { start: toYMD(new Date(y, m - 1, 1)), end: toYMD(new Date(y, m, 0)) };
    default:
      return null; // 'custom' -> el usuario fija las fechas
  }
};

// ── Detección del ciclo de la tarifa escalonada por volumen ──
//
// Espejo del `isFullCycle` de microservice-billing. Un comercio con escala solo se puede
// facturar cubriendo su ciclo COMPLETO (si no, el backend lo omite con `cycle_mismatch`),
// así que el wizard necesita saber qué ciclo es el período elegido para ofrecer solo los
// comercios que calzan.
//
// Es una duplicación deliberada y acotada: el backend valida igual y es la autoridad. Acá
// se replica para no ofrecer una tanda que se sabe que va a salir omitida.

/**
 * Fecha local desde un rótulo 'YYYY-MM-DD'.
 *
 * `new Date('2026-08-02')` lo parsea como medianoche UTC, así que en Chile (UTC−4) devuelve
 * el 1 de agosto y `getDay()` da el día equivocado. Construir con los componentes fuerza la
 * interpretación local, que es la que corresponde: el día de la semana de un rótulo de
 * fecha no depende de la zona horaria.
 */
const dateFromYMD = (ymd) => {
  if (typeof ymd !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return null;
  const [, y, mo, d] = m.map(Number);
  const dt = new Date(y, mo - 1, d);
  // Rechaza rótulos que existen como string pero no como día (2026-02-31).
  return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d
    ? dt
    : null;
};

/**
 * Ciclo que representa el rango, o `null` si no es un ciclo completo.
 *
 * @returns {'weekly' | 'monthly' | null}
 */
export const detectCycle = (start, end) => {
  const from = dateFromYMD(start);
  const to = dateFromYMD(end);
  if (!from || !to) return null;

  // Semana: domingo a sábado, 7 días exactos.
  if (from.getDay() === 0 && to.getDay() === 6 && toYMD(addDays(from, 6)) === toYMD(to)) {
    return 'weekly';
  }
  // Mes calendario: del 1 al último día del MISMO mes. `new Date(y, m + 1, 0)` es el día 0
  // del mes siguiente, o sea el último del actual: resuelve febrero y los bisiestos solo.
  const lastOfMonth = new Date(from.getFullYear(), from.getMonth() + 1, 0);
  if (from.getDate() === 1 && toYMD(to) === toYMD(lastOfMonth)) return 'monthly';

  return null;
};

/**
 * ¿Un comercio con escala `scale` se puede facturar en un período de ciclo `cycle`?
 *
 * Los comercios de precio plano (`'flat'`) se pueden facturar SIEMPRE: no tienen ciclo que
 * respetar, así que entran en cualquier tanda.
 */
export const scaleFitsCycle = (scale, cycle) =>
  !scale || scale === 'flat' || scale === cycle;
